import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

const tagVariants = cva(
  [
    // Layout
    'inline-flex items-center justify-center',
    // Typography
    'text-xs font-medium leading-none tracking-wide',
    // Interactive
    'cursor-pointer transition-colors',
  ],
  {
    variants: {
      variant: {
        filled: [
          'bg-this-solid text-white', // Default
          'hover:bg-this-solid-hover', // Hover
          'focus-visible:bg-this-solid-hover', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
          'disabled:opacity-50 disabled:pointer-events-none', // Disabled
        ],
        outlined: [
          'border border-this-line text-this-text', // Default
          'hover:border-this-line-hover hover:bg-this-bg', // Hover
          'focus-visible:border-this-line-hover focus-visible:bg-this-bg', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
          'disabled:opacity-50 disabled:pointer-events-none', // Disabled
        ],
        soft: [
          'bg-this-bg text-this-text', // Default
          'hover:bg-this-solid hover:text-white', // Hover
          'focus-visible:bg-this-solid focus-visible:text-white', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
          'disabled:opacity-50 disabled:pointer-events-none', // Disabled
        ],
        'soft-outlined': [
          'bg-this-bg border border-this-line text-this-text', // Default
          'hover:bg-this-solid hover:border-this-solid hover:text-white', // Hover
          'focus-visible:bg-this-solid focus-visible:border-this-solid focus-visible:text-white', // Focus (keyboard)
          'active:opacity-90', // Active/Pressed
          'disabled:opacity-50 disabled:pointer-events-none', // Disabled
        ],
      },
      size: {
        md: 'px-2 py-1',
        sm: 'h-4 min-w-4 px-1 py-0',
      },
      rounded: {
        default: 'rounded-sm',
        full: 'rounded-full',
      },
      glow: {
        true: 'shadow-lg shadow-this-solid/50',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'filled',
      size: 'md',
      rounded: 'default',
      glow: false,
    },
  }
);

type TagProps<E extends ElementType = 'button'> = {
  as?: E;
  color?: ColorType;
  children?: ReactNode;
} & VariantProps<typeof tagVariants> &
  Omit<ComponentPropsWithoutRef<E>, 'color'>;

const TagImpl = forwardRef(
  (
    {
      as,
      className,
      variant,
      size,
      rounded,
      color = 'neutral',
      glow,
      children,
      ...rest
    }: TagProps & { as?: ElementType },
    ref: React.ForwardedRef<HTMLElement>
  ) => {
    const Component = as || 'button';

    return (
      <Component
        ref={ref as React.Ref<never>}
        {...(Component === 'button' ? { type: 'button' } : {})}
        className={cn(
          tagVariants({ variant, size, rounded, glow }),
          setThisClass(color),
          className
        )}
        {...rest}
      >
        {children}
      </Component>
    );
  }
);

TagImpl.displayName = 'Tag';

// Type assertion for proper polymorphic typing (pattern used by Radix/Chakra)
const Tag = TagImpl as <E extends ElementType = 'button'>(
  props: TagProps<E> & { ref?: React.ComponentPropsWithRef<E>['ref'] }
) => React.ReactElement | null;

export { Tag };
export type { TagProps };
